import { type ReactNode, type SelectHTMLAttributes, useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "../icons";
import { controlClass, Field, fieldState } from "./Field";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Native select in the design frame; `placeholder` adds a disabled empty option. */
export function Select({
  label,
  hint,
  error,
  options,
  placeholder,
  id: givenId,
  className = "",
  ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string | true;
}) {
  const { t } = useTranslation("components");
  const generated = useId();
  const id = givenId ?? generated;
  const { invalid, describedBy } = fieldState(id, error, hint);
  return (
    <Field
      id={id}
      className={className}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(invalid ? { error } : {})}
    >
      <div className="relative">
        <select
          {...rest}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          data-component="Select"
          className={controlClass(invalid, "h-control appearance-none pr-8")}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder === true ? t("select.placeholder") : placeholder}
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          size={14}
          className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-ink-2"
        />
      </div>
    </Field>
  );
}
